/**
 * Voice Message Controls Component
 *
 * A simple icon button that synthesizes and plays text-to-speech for chat messages.
 * Shows only play button inline. When playing, displays a floating control panel above.
 * Uses OpenAI TTS - ephemeral audio, no persistent storage (HIPAA compliant).
 */

import * as React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:voice-message-controls');
import { createPortal } from 'react-dom';
import { useOpenAITTS, getPreferredVoice } from '../hooks/use-openai-tts';
import { IconButton } from './icon-button';
import { Tooltip } from './tooltip';
import { ENABLE_OPENAI_TTS } from '../env';
import type { TTSVoice } from '@ctrl-enter/types';

export interface VoiceMessageControlsProps {
  /** The text to synthesize and play */
  text: string;
  /** Optional voice ID to use (falls back to default) */
  voiceId?: TTSVoice;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Format seconds to MM:SS
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Voice Message Controls
 *
 * Renders inline play button. Shows floating control panel when audio is playing.
 * Audio is ephemeral - destroyed when panel closes (no persistent caching).
 */
export const VoiceMessageControls: React.FC<VoiceMessageControlsProps> = ({
  text,
  voiceId,
  className,
}) => {
  // Feature flag check - return null if TTS is disabled
  if (!ENABLE_OPENAI_TTS) {
    return null;
  }

  // Track if panel should be visible (separate from audio state)
  const [isPanelVisible, setIsPanelVisible] = React.useState(false);

  const voice = useOpenAITTS({
    voiceId: voiceId || getPreferredVoice(),
    autoPlay: true, // Auto-play after synthesis
    onError: error => {
      logger.error('Voice synthesis error:', JSON.stringify(error, null, 2));
    },
  });

  // Show panel when audio becomes active (playing, paused, or stopped with audio ready)
  React.useEffect(() => {
    if (
      (voice.state === 'playing' || voice.state === 'paused' || voice.state === 'stopped') &&
      !voice.isLoading
    ) {
      setIsPanelVisible(true);
    } else if (voice.state === 'idle' || voice.state === 'error') {
      // Hide panel when returning to idle or on error
      setIsPanelVisible(false);
    }
  }, [voice.state, voice.isLoading]);

  // Handler for inline play button - always starts/opens player
  const handleOpenPlayer = () => {
    if (voice.isLoading) {
      return;
    }
    // Always speak (which will either fetch or replay)
    voice.speak(text);
  };

  // Handler for play/pause button inside the panel
  const handlePlayPause = () => {
    if (voice.state === 'stopped') {
      // Audio at end - replay from start
      voice.resume();
    } else {
      // Playing or paused - toggle
      voice.toggle();
    }
  };

  const handleStop = () => {
    voice.stop();
  };

  const handleClose = () => {
    // Reset audio completely (destroy) and hide panel
    voice.reset();
    setIsPanelVisible(false);
  };

  const handleSeekForward = () => {
    voice.seekForward(5);
  };

  const handleSeekBackward = () => {
    voice.seekBackward(5);
  };

  const isDev = process.env.NODE_ENV !== 'production';

  return (
    <>
      {/* Inline Play Button - Always visible */}
      <div className={`flex items-center gap-1 ${className || ''}`}>
        <Tooltip content={voice.isLoading ? 'Generating audio...' : 'Play audio'} side="right">
          <IconButton
            iconName={voice.isLoading ? 'Loader2' : 'Volume2'}
            size="sm"
            variant="ghost"
            onClick={handleOpenPlayer}
            disabled={voice.isLoading || !text.trim()}
            aria-label="Play audio"
            className={voice.isLoading ? 'animate-spin' : ''}
          />
        </Tooltip>

        {/* Error Display - Inline */}
        {voice.error && (
          <div className="text-xs text-red-500 max-w-xs break-words p-2 bg-red-50 rounded border border-red-200">
            <strong>Error:</strong> {voice.error.message}
          </div>
        )}
      </div>

      {/* Floating Control Panel - Rendered via Portal */}
      {isPanelVisible &&
        createPortal(
          <div className="voice-controls-panel fixed top-4 left-1/2 -translate-x-1/2 z-50 glass-surface-static-solid-bg glass-surface-border border border-border/50 rounded-2xl shadow-lg p-2 sm:p-3 min-w-[280px] sm:min-w-[400px] max-w-[calc(100vw-32px)] animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col gap-2 sm:gap-3">
              {/* Header - Hidden on mobile */}
              <div className="hidden sm:flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Voice Playback</span>
                <Tooltip content="Close and stop" side="left">
                  <IconButton
                    iconName="X"
                    size="sm"
                    variant="ghost"
                    onClick={handleClose}
                    aria-label="Close panel and stop audio"
                  />
                </Tooltip>
              </div>

              {/* Progress Bar */}
              <div className="flex items-center gap-2 px-6 sm:px-0">
                {/* Time labels - Hidden on mobile */}
                <span className="hidden sm:inline text-xs text-muted-foreground font-mono">
                  {formatTime(voice.currentTime)}
                </span>
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-100"
                    style={{ width: `${voice.progress}%` }}
                  />
                </div>
                <span className="hidden sm:inline text-xs text-muted-foreground font-mono">
                  {formatTime(voice.duration)}
                </span>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                {/* Seek Backward */}
                <Tooltip content="Back 5s" side="bottom">
                  <IconButton
                    iconName="Rewind"
                    size="sm"
                    variant="ghost"
                    onClick={handleSeekBackward}
                    aria-label="Seek backward 5 seconds"
                  />
                </Tooltip>

                {/* Play/Pause */}
                <Tooltip content={voice.isPlaying ? 'Pause' : 'Play'} side="bottom">
                  <IconButton
                    iconName={voice.isPlaying ? 'Pause' : 'Play'}
                    size="md"
                    variant="default"
                    onClick={handlePlayPause}
                    aria-label={voice.isPlaying ? 'Pause' : 'Play'}
                  />
                </Tooltip>

                {/* Seek Forward */}
                <Tooltip content="Forward 5s" side="bottom">
                  <IconButton
                    iconName="FastForward"
                    size="sm"
                    variant="ghost"
                    onClick={handleSeekForward}
                    aria-label="Seek forward 5 seconds"
                  />
                </Tooltip>

                {/* Stop */}
                <Tooltip content="Stop" side="bottom">
                  <IconButton
                    iconName="Square"
                    size="sm"
                    variant="ghost"
                    onClick={handleStop}
                    disabled={voice.state === 'stopped'}
                    aria-label="Stop audio"
                  />
                </Tooltip>

                {/* Close button - visible on mobile only */}
                <div className="sm:hidden border-l border-border/50 pl-1 ml-1">
                  <IconButton
                    iconName="X"
                    size="sm"
                    variant="ghost"
                    onClick={handleClose}
                    aria-label="Close"
                  />
                </div>
              </div>

              {/* Debug Info (Dev Only - Hidden on mobile) */}
              {isDev && (
                <div className="hidden sm:block text-xs text-muted-foreground border-t border-border pt-2">
                  <div className="grid grid-cols-2 gap-1">
                    <span>State: {voice.state}</span>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
